import React from 'react';

import Button from '@/components/common/Button';
import { RightContext } from '@/components/RightProvider/context';

import { imperativeModal } from '../ImperativeModal';
import { aiCreditModalShow } from './aicredit';
import { contactUsShow } from './ContactUs';
import { createAvatarModalShow } from './createAvatar';
import { createVoiceModalShow } from './createVoice';
import { distributionModalShow } from './distribution';
import { exchangeCodeModalShow } from './exchangeCode';
import { firstOrderModalShow } from './firstOrder';
import { forgetPasswordModalShow } from './forgetPassword';
import CloseIcon from './icon/close';
import { loginModalShow } from './login';
import { paymentModalShow } from './payment';
import { renameAssertModalShow } from './renameAsset';
import { selectAvatarModalShow } from './selectAvatar';
import styles from './style.module.scss';
import { UploadModalShow } from './upload';
import { viralTemplateModalShow } from './viralTemplate';

interface CommonModalOptions {
  width: number | string;
  modalClassName?: string;
  titleClassName?: string;
  contentWrapperStyle?: {
    [propname: string]: string | number;
  };
  destroyOnHidden?: boolean;
  contentWrapperClassName?: string;
  title?: string | React.ReactNode;
  content: (
    close: () => void,
    contentRef: React.RefObject<any>,
    t: (key: string, value?: Record<string, any>) => string,
    resolve: (value: boolean | PromiseLike<boolean>) => void,
  ) => React.ReactNode;
  footer?: boolean;
  footerSlot?: (close: () => void) => React.ReactNode;
  cancelText?: string;
  onCancel?: (contentRef: React.RefObject<any>) => void;
  onConfirm?: (close: () => void, contentRef: React.RefObject<any>) => void;
  confirmText?: string;
  customPadding?: {
    x: number;
    y: number;
  };
  /**
   * 是否需要登录校验，默认为 true
   * 设置为 false 则跳过登录检查，直接显示弹窗内容
   */
  requireLogin?: boolean;
}

// 登录守卫组件
const LoginGuard: React.FC<{
  children: React.ReactNode;
  requireLogin: boolean;
  onLoginFailed?: () => void;
}> = ({ children, requireLogin, onLoginFailed }) => {
  const rightContext = React.useContext(RightContext);
  const [isReady, setIsReady] = React.useState(!requireLogin);

  React.useEffect(() => {
    // 不需要登录，直接显示内容
    if (!requireLogin) {
      return;
    }

    rightContext
      .getUserLoginState({
        onReady() {
          setIsReady(true);
        },
      })
      .then((isLoggedIn) => {
        if (!isLoggedIn) {
          onLoginFailed?.();
        }
      });
  }, []);

  // 等待准备完成
  if (!isReady) {
    return null;
  }

  return <>{children}</>;
};

function show(
  options: (
    t: (key: string, value?: Record<string, any>) => string,
  ) => CommonModalOptions,
  destroy?: React.MutableRefObject<any> | any,
): Promise<boolean> {
  const {
    width,
    modalClassName,
    titleClassName,
    contentWrapperClassName,
    contentWrapperStyle,
  } = options((key) => key);

  return new Promise((resolve) => {
    const destroyHandler = imperativeModal.show({
      antdModalProps: {
        open: true,
        width,
        className: `no-padding-modal ${styles['common-modal']} ${modalClassName || ''} ${modalClassName ? styles[modalClassName] : ''}`,
        title: null,
        centered: true,
        closeIcon: false,
        maskClosable: false,
        keyboard: false,
        destroyOnHidden: true,
      },
      renderContent: (closeModal, contentRef, t) => {
        const {
          title,
          content,
          cancelText,
          confirmText,
          footer = true,
          footerSlot,
          onCancel,
          onConfirm,
          customPadding = {
            x: 16,
            y: 8,
          },
          requireLogin = true, // 默认需要登录
        } = options(t);
        const cancel = () => {
          onCancel && onCancel(contentRef);
          closeModal();
          resolve(false);
        };

        const confirm = () => {
          const confirmCallback = () => {
            closeModal();
            resolve(true);
          };
          onConfirm && onConfirm(confirmCallback, contentRef);
        };

        const modalContent = (
          <div className="modal-content">
            {title ? (
              <div
                className={`h-[56px] px-[16px] flex flex-row items-center justify-center relative ${titleClassName}`}
              >
                {typeof title === 'string' ? (
                  <span className="text-[20px] font-[700] text-common-text-base-color inline-block w-[400px] truncate text-center">
                    {title}
                  </span>
                ) : (
                  title
                )}
                <div
                  className="absolute top-[17px] right-[24px] flex flex-row items-center justify-center cursor-pointer"
                  onClick={cancel}
                >
                  <CloseIcon />
                </div>
              </div>
            ) : null}
            <div
              className={`w-full ${contentWrapperClassName || ''}`}
              style={{
                padding: `${customPadding.y}px ${customPadding.x}px`,
                ...contentWrapperStyle,
              }}
            >
              {content(closeModal, contentRef, t, resolve)}
            </div>
            {footer && (
              <div className="w-full h-[60px] py-[5px] flex flex-row items-center justify-center">
                {footerSlot ? (
                  <div className="w-full h-full flex flex-row items-center gap-[8px]">
                    {footerSlot(closeModal)}
                  </div>
                ) : (
                  <div className="flex flex-row items-center justify-center gap-[10px] ">
                    {cancelText ? (
                      <Button
                        onClick={cancel}
                        style={{
                          border: '1px solid #A6A6A6',
                          fontSize: '18px',
                        }}
                        className="!bg-[transparent] !text-[#4931FA] !font-normal whitespace-nowrap !px-[40px] !py-[6px] !rounded-[6px] min-w-[142px]"
                      >
                        {cancelText}
                      </Button>
                    ) : null}
                    {confirmText ? (
                      <Button
                        style={{
                          fontSize: '18px',
                        }}
                        onClick={confirm}
                        className="!font-normal !bg-[#4931FA] whitespace-nowrap !px-[40px] !py-[6px] !rounded-[6px] min-w-[142px]"
                      >
                        {confirmText}
                      </Button>
                    ) : null}
                  </div>
                )}
              </div>
            )}
          </div>
        );

        return (
          <LoginGuard
            requireLogin={requireLogin}
            onLoginFailed={() => {
              closeModal();
              resolve(false);
            }}
          >
            {modalContent}
          </LoginGuard>
        );
      },
    });
    // 检查 handler 是否为 ref 对象（有 current 属性），如果是则更新其 current 值
    if (destroy && typeof destroy === 'object' && 'current' in destroy) {
      destroy.current = destroyHandler;
    }
  });
}

export default {
  show,
};

export const UploadModal = {
  show: UploadModalShow,
};

export const CreateAvatarModal = {
  show: createAvatarModalShow,
};

export const CreateVoiceModal = {
  show: createVoiceModalShow,
};

export const PaymentModal = {
  show: paymentModalShow,
};
export const ContactUsModal = {
  show: contactUsShow,
};

export const ExchangeCodeModal = {
  show: exchangeCodeModalShow,
};

export const AICreditModal = {
  show: aiCreditModalShow,
};

export const ForgetPasswordModal = {
  show: forgetPasswordModalShow,
};

export const LoginModal = {
  show: loginModalShow,
};

export const SelectAvatarModal = {
  show: selectAvatarModalShow,
};

export const RenameAssetModal = {
  show: renameAssertModalShow,
};

export const DistributionModal = {
  show: distributionModalShow,
};

export const FirstOrderModal = {
  show: firstOrderModalShow,
};

/** 爆款模板详情 / 复刻表单（命令式：`viralTemplateModal.show({ detail, creditCost })`） */
export const viralTemplateModal = {
  show: viralTemplateModalShow,
};
